// The comment-thread mark (#13194): a document comment is anchored to a run
// of the document's text by this mark, stored in the body as
// `<span data-comment-thread="<thread id>">`. The editor owns it like any
// other mark, so a hand edit that moves the text moves the comment, and the
// version history carries it. Two threads may cover the same text: the mark
// excludes nothing, not even itself.
//
// The highlight is not part of the mark. Which threads are open and which one
// is active is the host's state (the comments list), handed to the editor with
// `setCommentHighlights` and drawn as decorations, so it never reaches the
// saved HTML: a resolved thread keeps its mark in the body and simply is not
// drawn, and a read-only snapshot, whose host sets nothing, draws none.
import { Mark, mergeAttributes } from "@tiptap/core";
import type { Node as ProseMirrorNode, Slice } from "@tiptap/pm/model";
import {
  Plugin,
  PluginKey,
  type EditorState,
  type Transaction,
} from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { CommentSelection } from "./documentCommentsBridge";

export const COMMENT_THREAD_MARK = "commentThread";
/** The attribute the drawn highlight carries: the thread a click opens. */
export const COMMENT_HIGHLIGHT_ATTR = "data-comment-highlight";
/** The attribute the drawn quote of a comment being written carries. */
export const COMMENT_DRAFT_ATTR = "data-comment-draft";
/** The transaction meta the editor's loader sets on the `setContent` that
 *  brings a body in (a refetch, a body arriving after an empty load): the
 *  one change after which a comment draft's quote is looked for again. A
 *  reader's own select-all and typing replaces the document the same way
 *  and is an edit. */
export const COMMENT_BODY_LOAD_META = "fpCommentBodyLoad";

/**
 * The transaction meta `addCommentThread` sets, saying why a thread's mark
 * was added: `"new"` for a thread the reader just created (the body must be
 * saved with it), `"reanchor"` for a colleague's thread put back on its quote
 * in an editor with unsaved edits (no save of its own: the next save of the
 * reader's edits carries it).
 */
export const COMMENT_ANCHOR_META = "fpCommentAnchor";
export type CommentAnchorReason = "new" | "reanchor";

export interface CommentHighlightState {
  /** Threads that are open; only these are highlighted. */
  openThreadIds: readonly string[];
  /** The active thread, drawn darker; null for none. */
  activeThreadId: string | null;
  /** The text a comment is being written about, drawn in the active shade
   *  while the composer is open: the editor's selection went with the
   *  focus, and the thread's mark exists only once it is posted. The
   *  positions are where the selection was; once drawn, the editor moves
   *  the range with every edit, and the same draft object sent again does
   *  not move it back (only a new draft object is drawn afresh). Drawn
   *  afresh, the positions are used only while they still hold the quote,
   *  else the quote's first run: an editor remounted on a changed body is
   *  handed the same draft. Any edit that leaves the range not holding the
   *  quote ends it (it never jumps to the same words elsewhere), and the end
   *  is reported to the host (`endedCommentDraft`), which hands back the
   *  draft marked `ended`: drawn nothing, anchored nowhere. Only a body the
   *  editor's loader brings in (`COMMENT_BODY_LOAD_META`) is looked through
   *  again, nearest where the quote was. What is drawn is always
   *  where a submit anchors. */
  draft: CommentSelection | null;
}

type TextRange = { from: number; to: number };

/** The draft the host sent (compared by identity) and where its quote is in
 *  this editor's document now, if anywhere. */
interface HeldDraft {
  source: CommentSelection;
  /** Always holds the quote, or is null. */
  range: TextRange | null;
  /** An edit inside the quoted text ended the range: it is not looked for
   *  again. */
  ended: boolean;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    commentThread: {
      /** Put a thread's mark on a range, beside any marks already there. */
      addCommentThread: (
        range: { from: number; to: number },
        threadId: string,
        reason?: CommentAnchorReason
      ) => ReturnType;
      /** Which threads the editor draws as highlights, and the active one. */
      setCommentHighlights: (state: CommentHighlightState) => ReturnType;
    };
  }
}

type HighlightPluginState = Omit<CommentHighlightState, "draft"> & {
  draft: HeldDraft | null;
  decorations: DecorationSet;
};

const highlightKey = new PluginKey<HighlightPluginState>("commentHighlights");

/** Whether `range` lies in `doc` and holds exactly `quote`. */
const holdsQuote = (
  doc: ProseMirrorNode,
  range: TextRange,
  quote: string
): boolean =>
  range.from < range.to &&
  range.to <= doc.content.size &&
  doc.textBetween(range.from, range.to, " ") === quote;

/** A run of one text node that matches `quote`: the first, or with `near`
 *  the one starting nearest that position. Null when there is none. */
export const findQuoteRange = (
  doc: ProseMirrorNode,
  quote: string,
  near?: number
): TextRange | null => {
  if (!quote) return null;
  let best: TextRange | null = null;
  doc.descendants((node, pos) => {
    // With no position to be near, the first run found is the answer.
    if (best !== null && near === undefined) return false;
    if (!node.isText || !node.text) return true;
    for (
      let at = node.text.indexOf(quote);
      at >= 0;
      at = node.text.indexOf(quote, at + 1)
    ) {
      const found = { from: pos + at, to: pos + at + quote.length };
      if (near === undefined) {
        best = found;
        return false;
      }
      if (
        best === null ||
        Math.abs(found.from - near) < Math.abs(best.from - near)
      ) {
        best = found;
      }
    }
    return false;
  });
  return best;
};

/** Where a draft's quote is in `doc`: its selected positions while they
 *  still hold the quote, else the run of the quote nearest them, else
 *  nowhere. */
export const findDraftRange = (
  doc: ProseMirrorNode,
  draft: CommentSelection
): TextRange | null =>
  holdsQuote(doc, draft, draft.quote)
    ? { from: draft.from, to: draft.to }
    : findQuoteRange(doc, draft.quote, draft.from);

/** Whether the editor is drawing `draft`; its verdict on where the quote is
 *  (`commentDraftRange`) is then final. */
export const drawsDraft = (
  state: EditorState,
  draft: CommentSelection
): boolean => highlightKey.getState(state)?.draft?.source === draft;

/** Where the text `draft` quotes is now, as the editor has moved it with
 *  the edits since the draft was drawn: the range drawn, which always holds
 *  the quote. Null when the editor is not drawing that draft, the quote is
 *  nowhere, or an edit inside it ended it. */
export const commentDraftRange = (
  state: EditorState,
  draft: CommentSelection
): TextRange | null => {
  const held = highlightKey.getState(state)?.draft;
  return held && held.source === draft ? held.range : null;
};

/** The draft an edit has just ended, which the host has not yet been told
 *  of (it hands back the draft marked `ended`); null otherwise. */
export const endedCommentDraft = (
  state: EditorState
): CommentSelection | null => {
  const held = highlightKey.getState(state)?.draft;
  return held && held.ended && !held.source.ended ? held.source : null;
};

const sameRange = (a: TextRange | null, b: TextRange | null): boolean =>
  a === b || (a !== null && b !== null && a.from === b.from && a.to === b.to);

/** The thread ids whose marks the document carries. */
export const commentThreadIdsIn = (doc: ProseMirrorNode): Set<string> => {
  const ids = new Set<string>();
  doc.descendants((node) => {
    for (const mark of node.marks) {
      if (mark.type.name !== COMMENT_THREAD_MARK) continue;
      const id = mark.attrs.threadId as unknown;
      if (typeof id === "string" && id) ids.add(id);
    }
  });
  return ids;
};

/** Whether a transaction adds or removes a comment-thread mark: a mark
 *  step on one, or inserted content (a paste) that carries one. */
const changesCommentMarks = (tr: Transaction): boolean => {
  if (tr.getMeta(COMMENT_ANCHOR_META)) return true;
  return tr.steps.some((step) => {
    const json = step.toJSON() as {
      stepType?: string;
      mark?: { type?: string };
    };
    if (json.stepType === "addMark" || json.stepType === "removeMark") {
      return json.mark?.type === COMMENT_THREAD_MARK;
    }
    const slice = (step as unknown as { slice?: Slice }).slice;
    let carries = false;
    slice?.content.descendants((node) => {
      if (node.marks.some((m) => m.type.name === COMMENT_THREAD_MARK))
        carries = true;
      return !carries;
    });
    return carries;
  });
};

const drawHighlights = (
  doc: ProseMirrorNode,
  state: Omit<CommentHighlightState, "draft">,
  draft: TextRange | null
): DecorationSet => {
  const decorations: Decoration[] = [];
  if (draft && draft.from < draft.to && draft.to <= doc.content.size) {
    decorations.push(
      Decoration.inline(draft.from, draft.to, {
        nodeName: "span",
        class: "fp-comment-draft",
        [COMMENT_DRAFT_ATTR]: "",
      })
    );
  }
  if (state.openThreadIds.length === 0) {
    return DecorationSet.create(doc, decorations);
  }
  const open = new Set(state.openThreadIds);
  doc.descendants((node, pos) => {
    if (!node.isText) return;
    for (const mark of node.marks) {
      if (mark.type.name !== COMMENT_THREAD_MARK) continue;
      const id = mark.attrs.threadId as unknown;
      if (typeof id !== "string" || !open.has(id)) continue;
      decorations.push(
        Decoration.inline(pos, pos + node.nodeSize, {
          // A wrapper of its own per thread, so two threads on the same text
          // each keep their id (attributes without a node name merge).
          nodeName: "span",
          class:
            id === state.activeThreadId
              ? "fp-comment-highlight is-active"
              : "fp-comment-highlight",
          [COMMENT_HIGHLIGHT_ATTR]: id,
        })
      );
    }
  });
  return DecorationSet.create(doc, decorations);
};

export const CommentThreadMark = Mark.create({
  name: COMMENT_THREAD_MARK,
  // Overlapping threads: a thread's mark never pushes another one off.
  excludes: "",
  // Typing at the end of commented text does not extend the comment.
  inclusive: false,

  addAttributes() {
    return {
      threadId: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-comment-thread"),
        renderHTML: (attributes: { threadId?: string | null }) =>
          attributes.threadId
            ? { "data-comment-thread": attributes.threadId }
            : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-comment-thread]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, { class: "fp-comment-mark" }),
      0,
    ];
  },

  addCommands() {
    return {
      addCommentThread:
        (range, threadId, reason = "new") =>
        ({ tr, dispatch }) => {
          if (range.from >= range.to) return false;
          if (dispatch) {
            tr.addMark(range.from, range.to, this.type.create({ threadId }));
            tr.setMeta(COMMENT_ANCHOR_META, reason);
          }
          return true;
        },
      setCommentHighlights:
        (state) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(highlightKey, state);
            tr.setMeta("addToHistory", false);
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<HighlightPluginState>({
        key: highlightKey,
        state: {
          init: () => ({
            openThreadIds: [],
            activeThreadId: null,
            draft: null,
            decorations: DecorationSet.empty,
          }),
          apply: (tr, value, _oldState, newState) => {
            const next = tr.getMeta(highlightKey) as
              | CommentHighlightState
              | undefined;
            if (next) {
              // The same draft as before keeps the range the edits moved it
              // to (or, not found yet, is looked for again); a new one is
              // found where its selection was, or by its quote.
              const draft: HeldDraft | null =
                next.draft === null
                  ? null
                  : value.draft?.source === next.draft &&
                      (value.draft.range || value.draft.ended)
                    ? value.draft
                    : next.draft.ended
                      ? { source: next.draft, range: null, ended: true }
                      : {
                          source: next.draft,
                          range: findDraftRange(newState.doc, next.draft),
                          ended: false,
                        };
              const threads = {
                openThreadIds: next.openThreadIds,
                activeThreadId: next.activeThreadId,
              };
              return {
                ...threads,
                draft,
                decorations: drawHighlights(
                  newState.doc,
                  threads,
                  draft?.range ?? null
                ),
              };
            }
            if (!tr.docChanged) return value;
            // A body the loader brings in (`COMMENT_BODY_LOAD_META`) is
            // looked through again for a draft not ended, nearest where its
            // quote was; found or not, it is not ended. Any other change,
            // a reader's select-all and typing included, moves the range
            // with the text while it still holds the quote and ends it
            // otherwise; a draft with no range stays as it is (typing the
            // quote's words elsewhere does not attach it).
            const held = value.draft;
            const mapped: TextRange | null = held?.range
              ? {
                  from: tr.mapping.map(held.range.from, 1),
                  to: tr.mapping.map(held.range.to, -1),
                }
              : null;
            let draft: HeldDraft | null = held;
            if (
              held &&
              !held.ended &&
              tr.getMeta(COMMENT_BODY_LOAD_META) === true
            ) {
              draft = {
                source: held.source,
                range: findQuoteRange(
                  newState.doc,
                  held.source.quote,
                  held.range?.from ?? held.source.from
                ),
                ended: false,
              };
            } else if (held && held.range && mapped) {
              draft = holdsQuote(newState.doc, mapped, held.source.quote)
                ? { source: held.source, range: mapped, ended: false }
                : { source: held.source, range: null, ended: true };
            }
            const moved = { ...value, draft };
            // A change that adds or removes a comment mark, or that moved the
            // draft's range other than by the mapping, is drawn afresh; any
            // other edit only moves the highlights with the text.
            if (
              changesCommentMarks(tr) ||
              !sameRange(draft?.range ?? null, mapped)
            ) {
              return {
                ...moved,
                decorations: drawHighlights(
                  newState.doc,
                  moved,
                  draft?.range ?? null
                ),
              };
            }
            return {
              ...moved,
              decorations: value.decorations.map(tr.mapping, tr.doc),
            };
          },
        },
        props: {
          decorations: (state) => highlightKey.getState(state)?.decorations,
        },
      }),
    ];
  },
});
